import type { SubmissionView } from "@/types/api";
import Badge from "@/components/ui/Badge";
import { CheckCircle2, XCircle, User } from "lucide-react";

interface SubmissionCardProps {
  submission: SubmissionView;
  playerName: string;
  itemName: string;
  showActions: boolean;
  onApprove?: () => void;
  onReject?: () => void;
  acting?: boolean;
}

export default function SubmissionCard({
  submission,
  playerName,
  itemName,
  showActions,
  onApprove,
  onReject,
  acting,
}: SubmissionCardProps) {
  return (
    <div className="overflow-hidden rounded-2xl border-2 border-border bg-surface transition-all">
      <img
        src={submission.imageUrl}
        alt={itemName}
        className="aspect-square w-full object-cover"
        loading="lazy"
      />
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-base font-bold text-text">{itemName}</p>
            <p className="inline-flex items-center gap-1.5 text-sm text-text-secondary">
              <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10">
                <User className="h-3 w-3 text-primary" />
              </span>
              {playerName}
            </p>
          </div>
          <Badge
            variant={
              submission.moderationStatus === "approved"
                ? "accent"
                : submission.moderationStatus === "pending"
                  ? "warning"
                  : "error"
            }
          >
            {submission.moderationStatus}
          </Badge>
        </div>
        {submission.rejectionReason && (
          <p className="mt-1 text-sm text-error">
            {submission.rejectionReason}
          </p>
        )}
        {showActions && submission.moderationStatus === "pending" && (
          <div className="mt-4 flex gap-3">
            <button
              onClick={onApprove}
              disabled={acting}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-accent py-3 text-base font-bold text-white transition-all hover:bg-accent-hover active:scale-95 disabled:opacity-50"
            >
              <CheckCircle2 className="h-5 w-5" />
              Approve
            </button>
            <button
              onClick={onReject}
              disabled={acting}
              className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-error py-3 text-base font-bold text-white transition-all hover:opacity-90 active:scale-95 disabled:opacity-50"
            >
              <XCircle className="h-5 w-5" />
              Reject
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
